import api from './api';
import { ApiResponse } from '../types';
import {
  LetterV2,
  LettersListResponse,
  StudentLettersSummary,
  IssueLetterPayload,
  LetterPaymentStatus,
  LetterStatus
} from '../types/letters';

const buildQueryString = (params: Record<string, string | number | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') search.append(k, String(v));
  }
  return search.toString();
};

export interface LetterListFilters {
  category?: string;
  status?: LetterStatus | 'all';
  paymentStatus?: LetterPaymentStatus | 'all';
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
  studentId?: string;
}

const listStudentLetters = async (studentId: string, filters: LetterListFilters = {}): Promise<LettersListResponse> => {
  const qs = buildQueryString({
    category: filters.category,
    status: filters.status === 'all' ? undefined : filters.status,
    paymentStatus: filters.paymentStatus === 'all' ? undefined : filters.paymentStatus,
    from: filters.from,
    to: filters.to,
    page: filters.page,
    limit: filters.limit
  });
  const res = await api.get<ApiResponse<LettersListResponse>>(`/student-letters/${studentId}?${qs}`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const getStudentLettersSummary = async (studentId: string): Promise<StudentLettersSummary> => {
  const res = await api.get<ApiResponse<StudentLettersSummary>>(`/student-letters/${studentId}/summary`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

/**
 * Placeholder dictionary the backend uses at issuance. Pulled once when the
 * issue dialog opens so the live preview matches the eventual issued letter
 * exactly — no FE/BE drift on which placeholders resolve to what.
 */
const getPreviewContext = async (studentId: string): Promise<Record<string, string>> => {
  const res = await api.get<ApiResponse<Record<string, string>>>(`/student-letters/${studentId}/preview-context`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data || {};
};

const issueLetter = async (studentId: string, payload: IssueLetterPayload): Promise<LetterV2> => {
  const res = await api.post<ApiResponse<LetterV2>>(`/student-letters/${studentId}`, payload);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const listLetters = async (filters: LetterListFilters = {}): Promise<LettersListResponse> => {
  const qs = buildQueryString({
    category: filters.category,
    status: filters.status === 'all' ? undefined : filters.status,
    paymentStatus: filters.paymentStatus === 'all' ? undefined : filters.paymentStatus,
    from: filters.from,
    to: filters.to,
    studentId: filters.studentId,
    page: filters.page,
    limit: filters.limit
  });
  const res = await api.get<ApiResponse<LettersListResponse>>(`/letters?${qs}`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const getLetter = async (id: string): Promise<LetterV2> => {
  const res = await api.get<ApiResponse<LetterV2>>(`/letters/${id}`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

/** What the browser needs to print a letter (backend getLetterPrint). */
export interface LetterPrint {
  /** "Fine Letter", "Termination Notice". */
  title: string;
  severity: 'positive' | 'neutral' | 'caution' | 'critical';
  /** Stable reference, e.g. FIN-2026-38F3A1. */
  ref: string;
  /** "1 November 2026", on the school's calendar. */
  issuedOn: string;
  content: string;
  student: { name: string; details: Array<{ label: string; value: string }> };
  cancelled: boolean;
}

const getLetterPrint = async (letterId: string): Promise<LetterPrint> => {
  const res = await api.get<ApiResponse<LetterPrint>>(`/letters/${letterId}/print`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const resolveLetter = async (id: string): Promise<LetterV2> => {
  const res = await api.patch<ApiResponse<LetterV2>>(`/letters/${id}/resolve`);
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const cancelLetter = async (id: string, reason: string): Promise<LetterV2> => {
  const res = await api.patch<ApiResponse<LetterV2>>(`/letters/${id}/cancel`, { reason });
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const waiveFine = async (id: string, amount: number, reason: string): Promise<LetterV2> => {
  const res = await api.patch<ApiResponse<LetterV2>>(`/letters/${id}/waive`, { amount, reason });
  if (!res.data.success) throw new Error(res.data.message);
  return res.data.data!;
};

const deleteLetter = async (id: string): Promise<void> => {
  const res = await api.delete<ApiResponse<LetterV2>>(`/letters/${id}`);
  if (!res.data.success) throw new Error(res.data.message);
};

const letterService = {
  listStudentLetters,
  getStudentLettersSummary,
  getPreviewContext,
  issueLetter,
  listLetters,
  getLetter,
  getLetterPrint,
  resolveLetter,
  cancelLetter,
  waiveFine,
  deleteLetter
};

export default letterService;
